import React, { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";

/**
 * أدوات الحركة المشتركة (نفس تأثيرات الأقسام التانية) عشان متتكررش في كل صفحة:
 * Reveal / MaskLine / WordsScroll / WordsAuto / CountUp
 */
export const EASE = [0.76, 0, 0.24, 1];

// أي عنصر بيظهر (fade + طلوع خفيف) لما يدخل الشاشة، ويتعاد كل ما ترجعله
export const Reveal = ({ as = "div", delay = 0, y = 32, amount = 0.2, children, ...rest }) => {
  const Comp = motion[as];
  return (
    <Comp
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount }}
      transition={{ duration: 0.95, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

// سطر العنوان بيطلع من ورا قناع. show بتيجي من useInView على العنوان الأب (مش المقصوص)
export const MaskLine = ({ children, delay = 0, show }) => (
  <span className="mask">
    <motion.span
      className="mask__inner"
      initial={false}
      animate={{ y: show ? 0 : "115%" }}
      transition={{ duration: 1.1, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// كل كلمة بتنور مع السكرول (وبتطفي لو رجعت لفوق)
const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return (
    <motion.span className="word" style={{ opacity }}>
      {children}
    </motion.span>
  );
};

export const WordsScroll = ({ text, as: Tag = "p", className, offset = ["start 0.88", "end 0.6"] }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset });
  const words = text.split(" ");
  return (
    <Tag ref={ref} className={className}>
      {words.map((w, i) => (
        <React.Fragment key={`${w}-${i}`}>
          <Word progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]}>
            {w}
          </Word>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
};

// نفس التأثير بس بيشتغل أول ما النص يظهر (للنص اللي بيبان من غير سكرول)
export const WordsAuto = ({ text, as: Tag = "p", className, delay = 0, step = 0.035 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const words = text.split(" ");
  return (
    <Tag ref={ref} className={className}>
      {words.map((w, i) => (
        <React.Fragment key={`${w}-${i}`}>
          <motion.span
            className="word"
            initial={false}
            animate={{ opacity: inView ? 1 : 0.16 }}
            transition={{ duration: 0.5, delay: inView ? delay + i * step : 0 }}
          >
            {w}
          </motion.span>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
};

// عدّاد بيعدّ من صفر لما يظهر، وبيتعاد
export const CountUp = ({ to, locale = "en-US", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.8 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) {
      setVal(0);
      return;
    }
    const c = animate(0, to, { duration: 1.8, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
    return () => c.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toLocaleString(locale, { useGrouping: false })}
      {suffix}
    </span>
  );
};